import styled, { css } from 'styled-components';
import {
  v2Colors, v2FontFamily, v2ReadableFontFamily, v2Breakpoints,
} from './styles/tokens';

const base = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: background-color 180ms ease, border-color 180ms ease,
    color 180ms ease, box-shadow 180ms ease, transform 120ms ease;

  /* Tripled selector so these outrank questionnaire.css's
     "button.btn.btn-questionnaire" (which forces 11px 8% padding / 17px
     bold and can't be edited — it's shared by 79 other screens).
     box-shadow lives here too: it's not set by that global, but keeping it
     on the same high-specificity selector guarantees primary and secondary
     render an identical resting shadow. */
  &&& {
    min-height: 44px;
    padding: 11px 55px;
    font-family: ${v2FontFamily};
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.01em;
    border-radius: 14px;
    box-shadow: 0 2px 4px rgba(16, 24, 40, 0.12), 0 10px 22px rgba(16, 24, 40, 0.16);
  }

  &&&:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 24, 40, 0.16), 0 16px 30px rgba(16, 24, 40, 0.22);
  }

  &&&:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  ${base}
  background-color: ${v2Colors.primary};
  border-color: ${v2Colors.primary};
  color: #fff;

  &:hover {
    background-color: ${v2Colors.primaryHover};
    border-color: ${v2Colors.primaryHover};
    color: #fff;
  }

  &&&:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(0, 166, 81, 0.3);
  }
`;

export const SecondaryButton = styled.button`
  ${base}
  background-color: #fff;
  border-color: ${v2Colors.border};
  color: ${v2Colors.text};

  &:hover {
    background-color: ${v2Colors.surface};
    border-color: ${v2Colors.borderStrong};
    color: ${v2Colors.text};
  }

  &&&:focus-visible {
    outline: none;
    border-color: ${v2Colors.borderStrong};
    box-shadow: 0 0 0 3px rgba(102, 112, 133, 0.25);
  }
`;

// Compact + orange — for a minor, occasional override action (e.g. "Forza
// Targa"), not a primary/secondary flow action, so it's deliberately
// smaller than the base size rather than sharing it.
export const WarningButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 30px;
  padding: 5px 12px;
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid ${v2Colors.warningBorder};
  background-color: ${v2Colors.warningSoft};
  color: ${v2Colors.warningText};
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 4px 10px rgba(16, 24, 40, 0.1);
  cursor: pointer;
  transition: background-color 180ms ease, border-color 180ms ease,
    color 180ms ease, box-shadow 180ms ease;

  &:hover,
  &:focus-visible {
    background-color: ${v2Colors.warningSoftHover};
    border-color: ${v2Colors.warning};
    color: ${v2Colors.warningTextHover};
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.12), 0 8px 16px rgba(16, 24, 40, 0.14);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Compact + green — for an inline "add another repeatable item" action
// (e.g. "Aggiungi Attività", "Aggiungi Persone") inside a field-array list.
// Same compact footprint as WarningButton (not a primary/secondary flow
// action), but reads as a positive/additive action rather than an override.
export const AddItemButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 7px 16px;
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid ${v2Colors.primaryBorder};
  background-color: ${v2Colors.primarySoft};
  color: ${v2Colors.primaryHover};
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 4px 10px rgba(16, 24, 40, 0.1);
  cursor: pointer;
  transition: background-color 180ms ease, border-color 180ms ease,
    color 180ms ease, box-shadow 180ms ease;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover,
  &:focus-visible {
    background-color: ${v2Colors.primarySoftHover};
    border-color: ${v2Colors.primary};
    color: ${v2Colors.primaryHover};
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.12), 0 8px 16px rgba(16, 24, 40, 0.14);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Small explanatory note under a WarningButton (e.g. "Forza Targa") — reads
// as a quiet aside, not a label, so it uses the readable body font, a
// muted-but-not-faint color, and a capped width so it wraps into a short
// centered block instead of one long line.
export const WarningHint = styled.small`
  display: block;
  white-space: nowrap;
  margin-top: 6px;
  font-family: ${v2ReadableFontFamily};
  font-size: 13px;
  line-height: 1.5;
  color: ${v2Colors.textMuted};
  text-align: center;

  /* One line on desktop; on narrow screens let it wrap into a short
     centered block instead of overflowing off the edge. */
  @media (max-width: ${v2Breakpoints.tablet}) {
    white-space: normal;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
  }
`;
